import { useCallback, useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { aiNav, aiNavAnchors } from "@/mocks/aiassist";
import { apiErrorText, apiUrl } from "@/lib/api";
import { onServerEvent } from "@/lib/events";
import { useAuth } from "./auth";
import { exportsApi, type ExportRun } from "./contacts-api";
import { NOT_READY_NOTE, exportDatasets } from "./dataexport-meta";
import { goAnchor } from "./app-nav";
import { locale, t, tn } from "@/lib/i18n";

/* «Экспорт данных»: выбор набора, запрос выгрузки и история запросов с сервера.
   CSV собирает worker; по готовности автору приходит уведомление, файл доступен 24 часа. */

const stamp = (iso: string) =>
  new Date(iso).toLocaleString(locale, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const STATUS: Record<ExportRun["status"], string> = { queued: t("В очереди"), running: t("Обработка"), done: t("Готово"), failed: t("Ошибка") };

function DataExportDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const { user } = useAuth();
  // выгрузка — владелец, администраторы или доп. право «Экспорт данных» (проверяет и сервер)
  const canExport = !!user && (user.role === "owner" || user.role === "admin" || user.permissions.includes("exports"));
  const [jobs, setJobs] = useState<ExportRun[]>([]);
  const [picked, setPicked] = useState(exportDatasets[0].id);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState("");
  const [stampText, setStampText] = useState("");
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 4600);
  };

  const load = useCallback(async (manual = false) => {
    try {
      setJobs(await exportsApi.history());
      if (manual) setStampText(t("обновлено только что"));
    } catch (err) {
      flash(apiErrorText(err));
    }
  }, []);

  useEffect(() => {
    if (!canExport) return;
    void load();
    // готовая выгрузка присылает уведомление — перечитываем историю
    return onServerEvent("notification", () => void load());
  }, [canExport, load]);

  const dataset = exportDatasets.find((d) => d.id === picked) ?? exportDatasets[0];
  const busy = jobs.some((j) => j.status === "queued" || j.status === "running");

  // запасной опрос, пока выгрузка готовится
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => void load(), 4000);
    return () => clearInterval(t);
  }, [busy, load]);

  const onNav = (id: string) => {
    if (id === "data-export") return;
    if (id === "ai-assist") {
      goAnchor("#ai-assist");
      return;
    }
    const anchor = aiNavAnchors[id];
    if (anchor) {
      goAnchor(anchor);
      return;
    }
    flash(t("Раздел «{label}» пока не открывается", { label: aiNav.flatMap((g) => g.items).find((x) => x.id === id)?.label ?? "" }));
  };

  const request = async () => {
    if (busy) {
      flash(t("Один экспорт уже готовится — дождитесь его окончания"));
      return;
    }
    if (!dataset.ready) {
      flash(t("Выгрузка «{name}» {note}", { name: dataset.label, note: NOT_READY_NOTE }));
      return;
    }
    setSending(true);
    try {
      const job = await exportsApi.start(dataset.id);
      setJobs((prev) => [job, ...prev.filter((j) => j.id !== job.id)]);
      setOpen(false);
      flash(t("Запрос на выгрузку «{name}» создан — когда файл будет готов, придёт уведомление", { name: dataset.label }));
    } catch (err) {
      flash(apiErrorText(err));
    } finally {
      setSending(false);
    }
  };

  const download = (j: ExportRun) => {
    if (!j.url) return;
    // ссылка с сервера уже с /api — приводим к адресу с учётом базового пути
    const a = document.createElement("a");
    a.href = apiUrl(j.url.replace(/^\/api/, ""));
    a.download = "";
    a.click();
    flash(t("Файл «{name}» скачивается — {rows}", { name: j.datasetLabel, rows: tn(j.rows, "строка", "строки", "строк") }));
  };

  const refresh = () => {
    if (busy) {
      flash(t("Выгрузка ещё готовится — статус обновится автоматически"));
      return;
    }
    void load(true);
  };

  const th = "py-2.5 pe-3 text-start font-normal align-middle last:pe-0";

  return (
    <Band id="data-export" tone="muted">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">
            {str(brand) || "Hub"} {t("/ настройки данных → экспорт данных")}
          </span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className="size-1.5 rounded-full bg-primary" aria-hidden />
            {t("Запросов: {n}", { n: canExport ? jobs.length : "—" })}
          </span>
        </div>

        <div className="grid lg:grid-cols-[14.5rem_minmax(0,1fr)]">
          {/* ── настройки ── */}
          <div className="hidden flex-col gap-4 border-border p-3 lg:flex lg:border-e">
            {aiNav.map((g) => (
              <div key={g.group} className="flex flex-col gap-0.5">
                <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{g.group}</p>
                {g.items.map((it) => {
                  const active = it.id === "data-export";
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => onNav(it.id)}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 rounded-btn px-2.5 py-2 text-start text-[12.5px] font-medium transition",
                        active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <i className={cn(it.icon, "text-base leading-none")} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{it.label}</span>
                      {it.badge ? (
                        <span className="rounded-full bg-amber-400/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-600">{it.badge}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))}

            <div className="mt-auto rounded-btn border border-border bg-background/60 p-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <i className="ri-shield-check-line text-sm leading-none" aria-hidden />
                {t("Доступы")}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                {t("Выгрузку запрашивают владелец, администраторы и сотрудники с правом «Экспорт данных». Файл доступен 24 часа, затем удаляется.")}
              </p>
            </div>
          </div>

          {/* ── содержимое окна ── */}
          <div className="min-w-0">
            <div className="flex gap-1.5 overflow-x-auto border-b border-border px-3.5 py-2 lg:hidden">
              {aiNav.flatMap((g) => g.items).map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onNav(it.id)}
                  className={cn(
                    "inline-flex flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                    it.id === "data-export" ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  <i className={it.icon} aria-hidden />
                  {it.label}
                </button>
              ))}
            </div>

            <div className="p-3.5 md:p-5">
              <div className="flex flex-wrap items-start gap-2.5">
                <span className="grid size-8 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
                  <i className="ri-download-cloud-2-line text-base leading-none" />
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-semibold leading-snug">{t("Экспорт данных")}</h3>
                  <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
                    {t("Экспорт записей рабочего пространства для аналитики и других целей.")}
                  </p>
                </div>
              </div>

              {/* выбор набора */}
              <div className="mt-4 max-w-md">
                <p className="text-[12px] font-medium text-muted-foreground">{t("Выберите данные для экспорта")}</p>
                <div className="relative mt-1.5">
                  <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    aria-haspopup="listbox"
                    className="flex h-10 w-full cursor-pointer items-center gap-2 rounded-btn border border-border bg-background px-3 text-start text-[12.5px] font-medium transition hover:border-primary/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                  >
                    <i className={cn(dataset.icon, "text-sm leading-none text-primary")} aria-hidden />
                    <span className="min-w-0 flex-1 truncate">{dataset.label}</span>
                    <i className={cn("ri-arrow-down-s-line text-base leading-none text-muted-foreground transition", open && "rotate-180")} aria-hidden />
                  </button>

                  {open ? (
                    <ul
                      role="listbox"
                      aria-label={t("Что экспортировать")}
                      className="absolute inset-x-0 top-[calc(100%+6px)] z-30 overflow-hidden rounded-btn border border-border bg-card p-1 shadow-lift"
                    >
                      {exportDatasets.map((d) => {
                        const active = d.id === picked;
                        return (
                          <li key={d.id} role="option" aria-selected={active}>
                            <button
                              type="button"
                              disabled={!d.ready}
                              onClick={() => {
                                setPicked(d.id);
                                setOpen(false);
                              }}
                              className={cn(
                                "flex w-full cursor-pointer items-start gap-2.5 rounded-btn px-2.5 py-2 text-start transition disabled:cursor-not-allowed disabled:opacity-50",
                                active ? "bg-primary/10" : "hover:bg-muted disabled:hover:bg-transparent",
                              )}
                            >
                              <i className={cn(d.icon, "mt-0.5 text-sm leading-none", active ? "text-primary" : "text-muted-foreground")} aria-hidden />
                              <span className="min-w-0 flex-1">
                                <span className={cn("block text-[12.5px] font-semibold", active && "text-primary")}>{d.label}</span>
                                <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">
                                  {d.ready ? d.hint : `${d.hint} — ${NOT_READY_NOTE}`}
                                </span>
                              </span>
                              {active ? <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden /> : null}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </div>
              </div>

              <p className="mt-4 max-w-2xl text-[12px] leading-relaxed text-muted-foreground">
                {t("После запроса данные подготовятся, а мы сообщим, когда файл будет готов к скачиванию. В выгрузку попадают все контакты, которые видны вам. Пока одна выгрузка готовится, новую запросить нельзя.")}
              </p>

              {!canExport ? (
                <p className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
                  <i className="ri-lock-line text-sm leading-none" aria-hidden />
                  {t("Выгрузка доступна владельцу, администраторам и сотрудникам с правом «Экспорт данных».")}
                </p>
              ) : null}

              <button
                type="button"
                onClick={() => void request()}
                disabled={!canExport || busy || sending || !dataset.ready}
                className="mt-3 inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-45"
              >
                <i className={cn(sending ? "ri-loader-4-line animate-spin" : "ri-download-2-line", "text-sm leading-none")} aria-hidden />
                {t("Экспорт данных")}
              </button>

              {jobs.length === 0 ? (
                <div className="mt-5 border-t border-border pt-10 pb-6 text-center">
                  <span className="relative mx-auto grid size-28 place-items-center rounded-full bg-foreground/[.04] text-muted-foreground" aria-hidden>
                    <i className="ri-file-chart-line text-4xl leading-none" />
                    <span className="absolute end-1 top-1 grid size-9 place-items-center rounded-full border border-border bg-card text-primary">
                      <i className="ri-download-cloud-line text-base leading-none" />
                    </span>
                  </span>
                  <p className="mt-5 text-[14px] font-semibold">{t("Экспортируйте первые данные")}</p>
                  <p className="mx-auto mt-1.5 max-w-md text-[12px] leading-relaxed text-muted-foreground">
                    {t("Экспорт данных позволяет выгружать контакты в CSV для аналитики в таблицах и BI. Диалоги и сообщения {note}.", { note: NOT_READY_NOTE })}
                  </p>
                </div>
              ) : (
                <div className="mt-5 border-t border-border pt-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h4 className="text-[13px] font-semibold">{t("История экспорта данных")}</h4>
                    <div className="flex items-center gap-2">
                      {stampText ? <span className="text-[11px] text-muted-foreground">{stampText}</span> : null}
                      <button
                        type="button"
                        onClick={refresh}
                        className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-2.5 text-[11.5px] font-semibold transition hover:border-primary/50 hover:text-primary"
                      >
                        <i className="ri-refresh-line text-[13px] leading-none" aria-hidden />
                        {t("Обновить")}
                      </button>
                    </div>
                  </div>

                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full min-w-[580px] border-collapse text-start">
                      <thead>
                        <tr className="border-b border-border">
                          <th scope="col" className={th}>
                            <span className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Дата запроса")}</span>
                          </th>
                          <th scope="col" className={th}>
                            <span className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Набор данных")}</span>
                          </th>
                          <th scope="col" className={cn(th, "hidden sm:table-cell")}>
                            <span className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Доступен до")}</span>
                          </th>
                          <th scope="col" className={th}>
                            <span className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Статус")}</span>
                          </th>
                          <th scope="col" className={cn(th, "text-end")}>
                            <span className="sr-only">{t("Файл")}</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {jobs.map((j) => {
                          const ds = exportDatasets.find((d) => d.id === j.dataset);
                          const ready = j.status === "done" && !!j.url;
                          return (
                            <tr key={j.id} className="border-b border-border/60 transition last:border-0 hover:bg-muted/40">
                              <td className="whitespace-nowrap py-2.5 pe-3 text-[12px] tabular-nums">{stamp(j.createdAt)}</td>
                              <td className="py-2.5 pe-3">
                                <div className="flex items-center gap-2">
                                  <i className={cn(ds?.icon ?? "ri-file-list-3-line", "text-[13px] leading-none text-primary")} aria-hidden />
                                  <span className="min-w-0">
                                    <span className="block truncate text-[12.5px] font-semibold leading-snug">{j.datasetLabel || ds?.label || t("Данные")}</span>
                                    <span className="mt-0.5 block text-[10.5px] text-muted-foreground">
                                      {j.status === "done"
                                        ? tn(j.rows, "строка", "строки", "строк")
                                        : j.status === "failed"
                                          ? j.error || t("выгрузка не удалась")
                                          : t("готовится…")}
                                    </span>
                                  </span>
                                </div>
                              </td>
                              <td className="hidden whitespace-nowrap py-2.5 pe-3 text-[12px] tabular-nums text-muted-foreground sm:table-cell">
                                {j.expired ? t("истёк") : j.expiresAt ? stamp(j.expiresAt) : "—"}
                              </td>
                              <td className="whitespace-nowrap py-2.5 pe-3">
                                <span
                                  className={cn(
                                    "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold",
                                    j.status === "done" && !j.expired && "bg-primary/10 text-primary",
                                    j.status === "done" && j.expired && "bg-foreground/[.06] text-muted-foreground",
                                    j.status === "failed" && "bg-red-500/10 text-red-500",
                                    (j.status === "queued" || j.status === "running") && "bg-amber-400/15 text-amber-600",
                                  )}
                                >
                                  {j.status === "done" ? (
                                    <i className={cn(j.expired ? "ri-time-line" : "ri-checkbox-circle-fill", "text-[12px] leading-none")} aria-hidden />
                                  ) : j.status === "failed" ? (
                                    <i className="ri-error-warning-fill text-[12px] leading-none" aria-hidden />
                                  ) : (
                                    <i className="ri-loader-4-line animate-spin text-[12px] leading-none" aria-hidden />
                                  )}
                                  {j.status === "done" && j.expired ? t("Истёк") : STATUS[j.status]}
                                </span>
                              </td>
                              <td className="whitespace-nowrap py-2.5 text-end">
                                <button
                                  type="button"
                                  onClick={() => download(j)}
                                  disabled={!ready}
                                  title={j.expired ? t("Файл хранится 24 часа — запросите выгрузку заново") : undefined}
                                  className="inline-flex h-7 cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn border border-border px-2 text-[11px] font-semibold transition hover:border-primary/50 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                  <i className="ri-download-2-line text-[12px] leading-none" aria-hidden />
                                  {t("Скачать")}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-2.5 text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-time-line text-[13px] leading-none text-primary" aria-hidden />
                      {t("Файл хранится 24 часа")}
                    </span>
                    <span className="tabular-nums">
                      {t("{n} · показаны последние 50", { n: tn(jobs.length, "запрос", "запроса", "запросов") })}
                    </span>
                  </div>
                </div>
              )}

            </div>

            {toast ? (
              <div
                role="status"
                className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
              >
                <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
                {toast}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Band>
  );
}

export default DataExportDemo;